import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { toast } from "react-hot-toast";
import { Landmark, Loader2 } from "lucide-react";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { API } from "../../constant/api";
import { useBankName } from "../../hooks/use-bank-name";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";

/** `nested` softens the scrim when opened on top of another dialog. */
const BankDetail = ({
  setOpenBank,
  bankId,
  banks,
  businessID,
  nested = false,
}: any) => {
  const queryClient = useQueryClient();
  const axiosInstance = useAuthAxios();

  const selectedBank = banks?.find((bank: any) => bank._id === bankId);

  const { bankName, error } = useBankName();

  useEffect(() => {
    if (error) {
      toast.error(errorMessage(error?.message || error?.data?.message));
    }
  }, [error]);

  const onError = (e: any) =>
    toast.error(errorMessage(e?.response?.data?.message || e?.message));

  // Both actions identify the account the same way.
  const bankRef = {
    accountNumber: selectedBank?.accountNumber,
    bankCode: selectedBank?.bankCode,
  };

  const completeDeleteBank = useMutation({
    mutationFn: async (data: any) => {
      const response = await axiosInstance.delete(API.deleteBank(businessID), {
        data,
      });
      return response.data;
    },
    onSuccess: (r) => {
      toast.success(r.message);
      queryClient.invalidateQueries({ queryKey: ["get-business"] });
      setOpenBank(false);
    },
    onError,
  });

  const completeMakeDefault = useMutation({
    mutationFn: async (data: any) => {
      const response = await axiosInstance.put(
        API.makeDefaultBank(businessID),
        data
      );
      return response.data;
    },
    onSuccess: (r) => {
      toast.success(r.message);
      queryClient.invalidateQueries({ queryKey: ["get-business"] });
      setOpenBank(false);
    },
    onError,
  });

  const matchedBankName = bankName(selectedBank?.bankCode);

  const busy = completeDeleteBank.isPending || completeMakeDefault.isPending;

  return (
    <Dialog open onOpenChange={(next) => !next && setOpenBank(false)}>
      <DialogContent
        overlayClassName={nested ? "bg-black/40" : undefined}
        className="font-manrope mds:max-w-[440px]"
      >
        <DialogHeader>
          <DialogTitle className="font-sora">Bank details</DialogTitle>
          <DialogDescription>Manage this payout account.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-center gap-4 rounded-xl border bg-muted/50 p-4">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
              <Landmark className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-[15px] font-semibold text-foreground">
                  {selectedBank?.accountName || "Unnamed account"}
                </p>
                {selectedBank?.isDefault && <Badge>Default</Badge>}
              </div>
              <p className="mt-0.5 truncate text-[13px] font-medium text-muted-foreground">
                {selectedBank?.accountNumber}
                {matchedBankName ? ` • ${matchedBankName}` : ""}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {!selectedBank?.isDefault && (
              <Button
                onClick={() => completeMakeDefault.mutate(bankRef)}
                disabled={busy}
                className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
              >
                {completeMakeDefault.isPending ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  "Make default bank"
                )}
              </Button>
            )}

            <Button
              variant="ghost"
              onClick={() => completeDeleteBank.mutate(bankRef)}
              disabled={busy}
              className="h-[52px] w-full rounded-xl text-[15px] font-semibold text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              {completeDeleteBank.isPending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                "Delete bank"
              )}
            </Button>
          </div>

          <p className="text-center text-[12px] font-medium text-muted-foreground">
            Your default bank is where settlements are paid out.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BankDetail;
